import * as fs from 'fs';
import * as path from 'path';
import { fileURLToPath } from 'url';
import { GitHubClient } from '../core/githubClient';
import { SmartAgentMatcher } from '../core/smartAgentMatcher';
import { ConsoleLogger, AgentMetadata } from '../core/types';
import { CodeValidator } from '../core/codeValidator';
import { getResourceLoader } from '../core/resourceLoader.js';
import { ConfigTemplateLoader } from '../core/configTemplateLoader.js';

// ES模块中获取__dirname
const __filename = fileURLToPath(import.meta.url);
const __dirname = path.dirname(__filename);

/**
 * 生成配置文件工具
 */
export async function generateConfig(args: {
    projectPath: string;
    agentIds?: string[];
    autoMatch?: boolean;
    updateMode?: 'merge' | 'overwrite'; // merge: 保留自定义内容, overwrite: 完全覆盖
    configId?: string; // 配置方案ID (如 strict)
}): Promise<{
    content: Array<{ type: string; text: string }>;
}> {
    const logger = new ConsoleLogger();
    
    try {
        // 验证路径
        if (!fs.existsSync(args.projectPath)) {
            return {
                content: [{
                    type: 'text',
                    text: JSON.stringify({
                        error: `项目路径不存在: ${args.projectPath}`
                    }, null, 2)
                }]
            };
        }

        const matcher = new SmartAgentMatcher(logger);
        const githubClient = new GitHubClient(logger);
        
        let selectedAgents: AgentMetadata[] = [];

        // 如果需要自动匹配
        if (args.autoMatch !== false) {
            logger.log('正在分析项目特征...');
            
            const workspaceFolder = {
                uri: { fsPath: args.projectPath },
                name: path.basename(args.projectPath),
                index: 0
            };
            
            const features = await matcher.analyzeProject(workspaceFolder);
            
            logger.log('正在匹配 Agents...');
            
            // 从 npm 包内部加载 Agents（统一资源加载）
            const resourceLoader = getResourceLoader(logger);
            const availableAgents = resourceLoader.loadAllAgents();
            
            if (availableAgents.length === 0) {
                throw new Error('无法加载 Agents，请检查 npm 包是否完整');
            }
            
            logger.log(`成功加载 ${availableAgents.length} 个 Agents`);
            selectedAgents = matcher.matchAgents(features, availableAgents);
            logger.log(`匹配到 ${selectedAgents.length} 个 Agents`);
            selectedAgents = selectedAgents.slice(0, 5); // 取前5个
        }
        
        // 如果指定了 agentIds，使用指定的
        if (args.agentIds && args.agentIds.length > 0) {
            logger.log(`使用指定的 Agents: ${args.agentIds.join(', ')}`);
            
            selectedAgents = [];
            const resourceLoader = getResourceLoader(logger);
            
            for (const id of args.agentIds) {
                const agentData = resourceLoader.loadAgent(id);
                if (agentData) {
                    selectedAgents.push(agentData.metadata);
                    logger.log(`✅ 加载 Agent: ${id}`);
                } else {
                    logger.error(`Agent ${id} 不存在`);
                }
            }
        }

        if (selectedAgents.length === 0) {
            return {
                content: [{
                    type: 'text',
                    text: JSON.stringify({
                        error: '未找到合适的 Agents'
                    }, null, 2)
                }]
            };
        }

        // 生成配置文件
        logger.log('正在生成配置文件...');
        
        const githubDir = path.join(args.projectPath, '.github');
        const configPath = path.join(githubDir, 'copilot-instructions.md');

        // 检测已有配置的自定义内容
        let existingCustomContent = '';
        let existingConfig = '';
        if (fs.existsSync(configPath)) {
            existingConfig = fs.readFileSync(configPath, 'utf-8');
            
            // 提取自定义章节（标记为 CUSTOM 的内容）
            const customMatch = existingConfig.match(/<!-- CUSTOM_START -->([\s\S]*?)<!-- CUSTOM_END -->/g);
            if (customMatch) {
                existingCustomContent = customMatch.join('\n\n');
            }
        }

        // 创建目录
        if (!fs.existsSync(githubDir)) {
            fs.mkdirSync(githubDir, { recursive: true });
        }
        
        const updateMode = args.updateMode || 'merge'; // 默认保护模式
        const projectName = path.basename(args.projectPath);
        const projectPath = args.projectPath;

        // v2.0.0: 使用模板加载器生成精简配置
        // 设计原则：配置文件只包含必要信息，规范内容由 MCP 工具动态加载
        const templateLoader = new ConfigTemplateLoader();
        
        let content = templateLoader.generateMinimalConfig({
            projectName,
            projectPath,
            agents: selectedAgents.map(a => ({
                id: a.id,
                title: a.title,
                description: a.description,
                path: a.path
            })),
            existingCustomContent: updateMode === 'merge' ? existingCustomContent : undefined
        });

        logger.log(`✅ 使用模板生成配置（精简模式）`);
        logger.log(`   模板目录: ${templateLoader.getTemplatesPath()}`);
        logger.log(`   模板可用: ${templateLoader.isTemplateDirectoryAvailable()}`);

        // 写入文件前进行验证 (v1.1.0)
        const validator = new CodeValidator(logger);
        const validation = validator.validateConfigContent(content);
        
        if (!validation.isValid) {
            logger.error('⚠️ 配置内容验证失败，尝试自动修复...');
            
            // 尝试自动修复
            const fixResult = validator.attemptAutoFix(content);
            if (fixResult.fixed) {
                content = fixResult.content;
                logger.log(`✅ 已自动修复 ${fixResult.changes.length} 个问题:`);
                fixResult.changes.forEach(change => logger.log(`   - ${change}`));
                
                // 重新验证
                const revalidation = validator.validateConfigContent(content);
                if (!revalidation.isValid) {
                    const report = validator.generateValidationReport(revalidation);
                    logger.error('❌ 自动修复后仍存在问题:');
                    logger.error(report);
                    
                    return {
                        content: [{
                            type: 'text',
                            text: JSON.stringify({
                                error: '配置文件验证失败',
                                validationReport: report,
                                message: '生成的配置文件存在语法错误，请检查并手动修复'
                            }, null, 2)
                        }]
                    };
                }
            } else {
                const report = validator.generateValidationReport(validation);
                logger.error(report);
                
                return {
                    content: [{
                        type: 'text',
                        text: JSON.stringify({
                            error: '配置文件验证失败',
                            validationReport: report,
                            message: '生成的配置文件存在语法错误且无法自动修复'
                        }, null, 2)
                    }]
                };
            }
        } else if (validation.warnings.length > 0) {
            logger.log('⚠️ 配置内容验证通过，但有以下警告:');
            validation.warnings.forEach(warning => {
                logger.log(`   - [${warning.type}] ${warning.message}`);
            });
        } else {
            logger.log('✅ 配置内容验证通过');
        }

        // 写入文件
        fs.writeFileSync(configPath, content, 'utf-8');

        // 更新 .gitignore
        const gitignorePath = path.join(args.projectPath, '.gitignore');
        if (fs.existsSync(gitignorePath)) {
            let gitignoreContent = fs.readFileSync(gitignorePath, 'utf-8');
            if (!gitignoreContent.includes('.github/copilot-instructions.md')) {
                gitignoreContent += '\n# Copilot Prompts (auto-generated)\n.github/copilot-instructions.md\n';
                fs.writeFileSync(gitignorePath, gitignoreContent, 'utf-8');
            }
        }

        logger.log(`✅ 配置文件已生成: ${configPath}`);

        return {
            content: [{
                type: 'text',
                text: JSON.stringify({
                    success: true,
                    configPath,
                    agents: selectedAgents.map(a => ({
                        id: a.id,
                        title: a.title,
                        score: a.score
                    })),
                    message: `已成功生成配置文件，应用了 ${selectedAgents.length} 个 Agents`
                }, null, 2)
            }]
        };
    } catch (error) {
        logger.error(`生成配置失败: ${error}`);
        return {
            content: [{
                type: 'text',
                text: JSON.stringify({
                    error: error instanceof Error ? error.message : String(error)
                }, null, 2)
            }]
        };
    }
}
